import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useKitsData } from "@/data/use-kits-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { GetKitDto } from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export default function ExpertsPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expertName, setExpertName] = useState("Unnamed Expert");

  const { kits, createKit } = useKitsData(groupUuid, {
    type: "expert",
  });

  const groupedExperts = groupByDate(kits, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  function handleCreateExpert() {
    if (expertName.trim()) {
      const name = expertName.trim();

      createKit({
        name,
      }).then(async (kit) => {
        if (!kit) {
          toast.error("Failed to create expert");
          return;
        }

        toast.success("Expert created successfully!");
        navigate({
          to: "/w/$groupSlug/k/$kitUuid/expert",
          params: { groupSlug, kitUuid: kit.uuid },
        });
      });

      setExpertName("");
      setIsDialogOpen(false);
    }
  }

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Workspace Expert AIs</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column>
          <p>
            This is where you build your own Expert AIs. Give each AI a distinct
            personality and define how they approach your documents and tasks.
            You can also teach them new skills, customize their name and icon to
            fit your team, and even share them with specific teams or groups.
          </p>
        </Column>
        <Row>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setIsDialogOpen(true)}>
                Create Expert AI
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create New Expert</DialogTitle>
              </DialogHeader>
              <Column className="gap-4 py-4">
                <Label htmlFor="expert-name">Name</Label>
                <Input
                  id="expert-name"
                  value={expertName}
                  onChange={(e) => setExpertName(e.target.value)}
                  placeholder="Name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleCreateExpert();
                    }
                  }}
                />
              </Column>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setExpertName("");
                  }}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button type="submit" onClick={handleCreateExpert} size="sm">
                  Create Expert AI
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Row>
        <GroupedTable
          data={groupedExperts}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => (
            <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
              <Button
                variant="ghost"
                size="sm"
                className="hover:bg-destructive hover:text-destructive-foreground"
                aria-label="Delete"
                title="Delete"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </KitDeletionDialog>
          )}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid/expert",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="No Expert AIs here yet. Create your first one to build your custom AI team and tackle your marketing goals."
        />
      </PageBody>
    </Page>
  );
}

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>
          {getInitials(getUserForUserUuid(userUuid)?.name || "", 2)}
        </AvatarFallback>
        <AvatarImage
          src={getUserForUserUuid(userUuid)?.photo_url ?? undefined}
        />
      </Avatar>
      <span>{getUserForUserUuid(userUuid)?.name}</span>
    </Row>
  );
}
